import { useCallback, useEffect, useRef } from "react";
import { api, ApiError } from "./api";
import { useSetParam } from "./nav";
import { useSession } from "../shell/store";
import type { TicketPurgeResult, Workspace } from "@omnabase/shared";
import { canProject, capsOf } from "./access";

/** The one-card round (2026-10-23; brief docs/briefs/mcp-tab-one-card.md): the inbox's two small additions that are not the list itself. */

/** "Deleted" in the views column's Team group: only while it holds something for the viewer (the founder: "shown only while it holds
 *  something, with a count, like Spam"), or while it is the view on screen */
export function deletedGroupItem(count: number | undefined, active: boolean, href: string) {
  if (!count && !active) return [];
  return [{ id: "deleted", label: "Deleted", icon: "trash" as const, href, active, count: count || undefined }];
}

/** "Unknown address" in the same group (the ticket page and marks round, 2026-10-23): the stray mail of the workspace, for administrators
 *  (only they get its count), only while it holds something or is on screen */
export function unknownAddressGroupItem(count: number | undefined, active: boolean, href: string) {
  if (!count && !active) return [];
  return [{ id: "unknown-address", label: "Unknown address", icon: "mail" as const, href, active, count: count || undefined }];
}

/** a deleted conversation back where it was */
export const restoreTicket = (slug: string, number: number) => api.post<{ ok: true }>(`/api/v1/w/${slug}/tickets/${number}/restore`);
/** a conversation into Deleted for 30 days */
export const deleteTicket = (slug: string, number: number) => api.del<{ ok: true; deletedAt: string }>(`/api/v1/w/${slug}/tickets/${number}`);

/** The inbox opens on the project the person chose last (`me.inboxProject`): the plain inbox gets its `?project=`; a link that names a
 *  project wins and changes nothing; only the chooser saves (the red team's item 17). Answers the chooser's save. */
export function useRememberedProject(projectId: string | null, multi: boolean): (id: string | null) => void {
  const { workspace, reload } = useSession();
  const setParam = useSetParam();
  const remembered = workspace.me.inboxProject ?? null;
  const cleared = useRef(false); // "All projects" was just chosen: the remembered one is on its way out, do not bring it back meanwhile
  useEffect(() => {
    if (projectId || !remembered || !multi || cleared.current) return;
    if (workspace.projects.some((p) => p.id === remembered && !p.archivedAt)) setParam("project", remembered);
  }, [projectId, remembered, multi]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!remembered) cleared.current = false; }, [remembered]);
  return useCallback((id: string | null) => {
    if (id === remembered) return;
    if (!id) cleared.current = true;
    void api.patch(`/api/v1/w/${workspace.slug}/me/settings`, { inboxProject: id }).then(() => reload(), () => { cleared.current = false; });
  }, [remembered, workspace.slug, reload]);
}

/** who may delete this conversation (the API's rule): the owner and administrators (`team`) any ticket they see, anyone else a ticket assigned
 *  to them in a project where they may reply */
export function mayDeleteTicket(workspace: Workspace, t: { assigneeId: string | null; projectId: string }, me: string): boolean {
  if (capsOf(workspace).has("team")) return true;
  const project = workspace.projects.find((p) => p.id === t.projectId);
  return t.assigneeId === me && !!project && canProject(workspace, project, "reply");
}

/** The delete-for-good round (2026-10-28): what the owner agreed to remove, from Deleted or Spam: chosen numbers, or the whole bin as the
 *  list shows it (`project` as the list's ?project=); `count` is the number they typed */
export type PurgeAsk = { view: "deleted" | "spam"; numbers?: number[]; count: number; project?: string };
export type PurgeDone = { removed: number; waiting: number; /** the bin changed before anything went: it holds this many now */ changedTo?: number };

/** Deletes for good, now. A bin over one batch is emptied call after call with the count the server says is left, after one confirmation;
 *  it stops when a call removed nothing (only conversations the AI still works on are left) or the bin changed meanwhile (409). A change
 *  before the first call answers the bin's new count, for the dialog to ask again. */
export async function purgeConversations(slug: string, ask: PurgeAsk): Promise<PurgeDone> {
  const waiting = new Set<number>(); let removed = 0; let confirm = ask.count;
  for (;;) {
    let r: TicketPurgeResult;
    try { r = await api.post<TicketPurgeResult>(`/api/v1/w/${slug}/tickets/purge`, { view: ask.view, confirm, ...(ask.numbers ? { numbers: ask.numbers } : {}), ...(ask.project ? { project: ask.project } : {}) }); }
    catch (e) { const changed = changedCount(e); if (changed === null) throw e; return removed ? { removed, waiting: waiting.size } : { removed, waiting: waiting.size, changedTo: changed }; }
    removed += r.removed; for (const s of r.skipped) waiting.add(s.number);
    if (ask.numbers || r.left === 0 || r.removed === 0) return { removed, waiting: waiting.size };
    confirm = r.left;
  }
}
const changedCount = (e: unknown): number | null => {
  const d = e instanceof ApiError && e.status === 409 ? (e.details as { reason?: string; count?: number } | undefined) : undefined;
  return d?.reason === "changed" && typeof d.count === "number" ? d.count : null;
};
